import { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import Keycloak, {
	KeycloakError,
	KeycloakInitOptions,
	KeycloakTokenParsed,
} from "keycloak-js";

type EnsureToken = (minValidity?: number) => Promise<string | undefined>;
export interface KeycloakAuth {
	kc: Keycloak;
	ensureToken: EnsureToken;
	doLogout: () => Promise<void>;
	getUser: () => KeycloakTokenParsed | undefined;
	getRawToken: () => string | undefined;
}

interface SetupOptions {
	keycloakUrl: string;
	realm: string;
	clientId: string;
	axiosInstances?: AxiosInstance[]; // e.g., [API, APIv2, APIv2_FormData]
	init?: Partial<KeycloakInitOptions>;
	sentryUserEmail?: boolean; // set true if you want Sentry.setUser(email)
}

let accessToken: string | undefined;
let tokenPromise: Promise<string | undefined> | null = null;

export async function setupKeycloakAuth(
	opts: SetupOptions,
): Promise<KeycloakAuth> {
	const kc = new Keycloak({
		url: opts.keycloakUrl,
		realm: opts.realm,
		clientId: opts.clientId,
	});

	const ensureToken: EnsureToken = async (minValidity = 10) => {
		if (tokenPromise) return tokenPromise;

		tokenPromise = kc
			.updateToken(minValidity)
			.then(() => {
				accessToken = kc.token ?? undefined;
				return accessToken;
			})
			.catch((e: KeycloakError) => {
				console.error("Failed to refresh token", e);
				// If refresh fails, end session and clear token
				accessToken = undefined;
				return kc
					.logout({ redirectUri: window.location.origin })
					.then(() => undefined)
					.catch(() => undefined);
			})
			.finally(() => {
				tokenPromise = null;
			});

		return tokenPromise;
	};

	// --- Keycloak event hooks ---
	kc.onAuthSuccess = () => {
		accessToken = kc.token ?? undefined;
	};

	kc.onTokenExpired = () => {
		// Try to refresh silently; on failure ensureToken will log out
		void ensureToken(10);
	};

	kc.onAuthLogout = () => {
		accessToken = undefined;
	};

	// --- Init ---
	await kc.init({
		onLoad: "login-required",
		flow: "standard",
		pkceMethod: "S256",
		checkLoginIframe: true,
		silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
		...opts.init,
	});

	// First token fetch (no-op if already present)
	await ensureToken(10);

	// --- Axios interceptors (optional but recommended) ---
	if (opts.axiosInstances?.length) {
		const addAuthHeader = async (config: InternalAxiosRequestConfig) => {
			const t = await ensureToken(10);
			if (t) {
				config.headers = config.headers ?? {};
				(config.headers as Record<string, string>)["Authorization"] =
					`Bearer ${t}`;
			}
			return config;
		};

		for (const inst of opts.axiosInstances) {
			inst.interceptors.request.use(addAuthHeader);
		}
	}

	return {
		kc,
		ensureToken,
		doLogout: async () => {
			accessToken = undefined;
			await kc.logout({ redirectUri: window.location.origin });
		},
		getUser: () => kc.tokenParsed ?? undefined,
		getRawToken: () => accessToken,
	};
}
